import api from '../../api/axiosConfig';

const MARKER_ATTR = 'data-instat-helper';
// 3500 ms (était 800). Le trajet d'un message = postMessage → app-bridge →
// chrome.runtime.sendMessage → RÉVEIL du service worker MV3 (cold start qui peut
// dépasser 1 s sur machine chargée) → storage.set → réponse. À 800 ms le timeout
// pouvait tomber avant l'écriture de la cible, et popup.location partait alors
// avant que la cible soit committée. Le timeout n'est qu'un plafond : coût nul en
// nominal (l'attente ne dure que le round-trip réel, ~10-50 ms SW chaud).
const REQUEST_TIMEOUT_MS = 3500;

// Toutes les ouvertures de popup Hudl partagent UNE seule cible (instatTarget)
// dans l'extension. Deux ouvertures concurrentes (double-clic, deux stats à la
// suite) se volent donc la cible → mauvais joueur, ou popup échoué sur l'accueil
// Hudl. On sérialise la partie asynchrone (fetch backend + prime + navigation)
// pour qu'une ouverture soit menée à terme avant que la suivante ne prime sa
// cible. Le popup lui-même reste ouvert de façon synchrone par l'appelant (anti
// popup-blocker) ; seul son « remplissage » est mis en file.
let openChain = Promise.resolve();
export function serializeExtensionOpen(task) {
  const run = openChain.then(task, task);
  openChain = run.catch(() => {});
  return run;
}

// Prime la cible vidéo, avec un retry unique si le 1er essai a expiré / n'a pas
// eu de réponse (service worker endormi). Évite que popup.location parte avec une
// cible non committée. Retourne le dernier résultat ({ok}).
export async function primeVideoTargetReliable(url, playerContext = null) {
  let r = await primeExtensionVideoTarget(url, playerContext);
  if (r && !r.ok && (r.error === 'timeout' || r.error === 'no-response')) {
    // eslint-disable-next-line no-console
    console.warn('[instat] prime-target a échoué (', r.error, ') — retry');
    r = await primeExtensionVideoTarget(url, playerContext);
  }
  if (r && !r.ok) {
    // eslint-disable-next-line no-console
    console.warn('[instat] prime-target non confirmé:', r.error);
  }
  return r;
}

export function isExtensionInstalled() {
  return document.documentElement.getAttribute(MARKER_ATTR) === '1';
}

function sendExtensionMessage(type, payload = {}) {
  return new Promise((resolve) => {
    if (!isExtensionInstalled()) {
      resolve({ ok: false, error: 'not-installed' });
      return;
    }
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const onMessage = (event) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || data.__instatHelper !== true || data.type !== 'reply') return;
      if (data.requestId !== requestId) return;
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(data);
    };
    window.addEventListener('message', onMessage);
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      resolve({ ok: false, error: 'timeout' });
    }, REQUEST_TIMEOUT_MS);
    window.postMessage(
      { __instatHelper: true, type, requestId, ...payload },
      window.location.origin,
    );
  });
}

export function pingExtension() {
  return sendExtensionMessage('ping');
}

export async function primeExtensionCredentials() {
  if (!isExtensionInstalled()) return { ok: false, error: 'not-installed' };
  try {
    const { data } = await api.get('/api/instat/browser-credentials');
    return sendExtensionMessage('prime', { email: data.email, password: data.password });
  } catch (e) {
    return { ok: false, error: e.response?.data?.error || e.message };
  }
}

// `playerContext` (optional) lets the extension tag any clip saved while the
// user is browsing this target URL with our local player_id + Hudl instat
// player_id, and (optionally) an informe_id. Without it, saved clips land in
// DB as orphans (no link to a scouting players row). Stored in
// chrome.storage.local with the same TTL as the target URL itself.
export function primeExtensionVideoTarget(url, playerContext = null) {
  if (!isExtensionInstalled()) return Promise.resolve({ ok: false, error: 'not-installed' });
  const payload = { url };
  if (playerContext) {
    if (playerContext.localPlayerId || playerContext.instatPlayerId) {
      payload.localPlayerId = playerContext.localPlayerId || null;
      payload.instatPlayerId = playerContext.instatPlayerId || null;
    }
    if (playerContext.informeId) {
      payload.informeId = playerContext.informeId;
    }
  }
  // eslint-disable-next-line no-console
  console.log('[instat] primeExtensionVideoTarget:', payload);
  return sendExtensionMessage('prime-target', payload);
}

// Push the user's Valencia session to the extension so it can POST to the
// backend from the Hudl page (Save clip button injection). Stored with a 4h
// TTL in chrome.storage.local.
export function primeExtensionAppAuth() {
  if (!isExtensionInstalled()) return Promise.resolve({ ok: false, error: 'not-installed' });
  const token = localStorage.getItem('token');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (!token || !backendUrl) return Promise.resolve({ ok: false, error: 'no-creds' });
  const clubId = localStorage.getItem('clubId');
  const clubSlug = localStorage.getItem('clubSlug');
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const section = process.env.REACT_APP_SECTION || 'hommes';
  return sendExtensionMessage('prime-app-auth', {
    backendUrl,
    token,
    clubId,
    clubSlug,
    userId: user?.id,
    userRole: user?.role,
    section,
  });
}
